import type { CalendarDate } from '@internationalized/date'
import cx from 'classnames'
import { CalendarCell as AriaCalendarCell } from 'react-aria-components'

import { focusRingStyles } from '../../../libs/focusRingStyles'
import { typographyStyles } from '../../../structure/typography/styles'

type Props = {
  date: CalendarDate
  isRange: boolean
}

export const CalendarCell = ({ date, isRange }: Props) => (
  <AriaCalendarCell
    date={date}
    className={({ isSelected, isSelectionEnd, isSelectionStart }) =>
      cx(
        'outline-hidden focus-visible:ring-0 focus-visible:inset-ring-0',
        '[td:first-child_&]:rounded-s-full [td:last-child_&]:rounded-e-full',
        'my-0.5',
        isSelected && isRange && 'bg-primary-500/10',
        isSelectionStart && 'rounded-s-full',
        isSelectionEnd && 'rounded-e-full',
      )
    }
  >
    {({
      formattedDate,
      isSelected,
      isDisabled,
      isUnavailable,
      isSelectionStart,
      isSelectionEnd,
      isOutsideMonth,
      isFocused,
    }) => {
      const isNonInteractive = isDisabled || isUnavailable
      const isSelectedCap =
        isSelected && (isSelectionStart || isSelectionEnd || !isRange)
      const solidStates =
        `${isSelectedCap ? 'selectedCap' : 'notSelectedCap'}${isNonInteractive ? 'Disabled' : 'Valid'}` as const
      return (
        <span
          className={cx(
            'size-8 flex justify-center items-center',
            isOutsideMonth && 'hidden', // react-aria prevents interaction with these, so hiding them entirely prevents confusion
            isFocused &&
              cx(
                focusRingStyles({ hasError: false, variant: 'controlled' }),
                'rounded-full',
              ),
            typographyStyles({
              intensity: isNonInteractive ? 'tertiary' : 'primary',
            }),
            {
              selectedCapValid:
                'rounded-full bg-primary-500 text-amaze-inkBright',
              selectedCapDisabled: '', // should never happen
              notSelectedCapValid: 'hover:bg-primary-500/20 rounded-full',
              notSelectedCapDisabled: 'cursor-not-allowed',
            }[solidStates],
          )}
        >
          {formattedDate}
        </span>
      )
    }}
  </AriaCalendarCell>
)
